/* -------------------------------------------------------------------------- */
/*                          Helpers génériques                                */
/* -------------------------------------------------------------------------- */

export function ensureArray(data) {
  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.data)) return data.data;

  return [];
}

/* -------------------------------------------------------------------------- */
/*                                 URL                                        */
/* -------------------------------------------------------------------------- */

function normalizeUrl(value) {
  const clean = String(value || "").trim();

  if (!clean) return "";

  if (
    clean.startsWith("http://") ||
    clean.startsWith("https://")
  ) {
    return clean;
  }

  return `https://${clean}`;
}

/* -------------------------------------------------------------------------- */
/*                           Coordonnées GPS                                  */
/* -------------------------------------------------------------------------- */

function parseGpsString(value) {
  const clean = String(value || "").trim();

  if (!clean) return null;

  const parts = clean
    .split(/[;,]/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length !== 2) {
    return null;
  }

  const lat = Number(parts[0]);
  const lng = Number(parts[1]);

  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lng)
  ) {
    return null;
  }

  if (
    lat < -90 ||
    lat > 90 ||
    lng < -180 ||
    lng > 180
  ) {
    return null;
  }

  /*
   * Evite les anciennes coordonnées parasites.
   */
  if (lat === 0 && lng === 0) {
    return null;
  }

  return {
    lat,
    lng,
  };
}

function parseSeparateGps(latValue, lngValue) {
  if (
    latValue === null ||
    latValue === undefined ||
    latValue === "" ||
    lngValue === null ||
    lngValue === undefined ||
    lngValue === ""
  ) {
    return null;
  }

  const lat = Number(latValue);
  const lng = Number(lngValue);

  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lng)
  ) {
    return null;
  }

  if (
    lat < -90 ||
    lat > 90 ||
    lng < -180 ||
    lng > 180
  ) {
    return null;
  }

  if (lat === 0 && lng === 0) {
    return null;
  }

  return {
    lat,
    lng,
  };
}

/* -------------------------------------------------------------------------- */
/*                          Google Maps                                       */
/* -------------------------------------------------------------------------- */

function buildMapsHref(lat, lng) {
  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lng)
  ) {
    return "";
  }

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${lat},${lng}`,
  )}`;
}

/* -------------------------------------------------------------------------- */
/*                             Adresse                                        */
/* -------------------------------------------------------------------------- */

function buildAddress(...parts) {
  return parts
    .map((part) =>
      String(part || "").trim(),
    )
    .filter(Boolean)
    .join(", ");
}

/* -------------------------------------------------------------------------- */
/*                             Emails                                         */
/* -------------------------------------------------------------------------- */

function normalizeEmails(value) {
  return String(value || "")
    .split(";")
    .map((email) => email.trim())
    .filter(Boolean);
}

/* -------------------------------------------------------------------------- */
/*                     Normalisation Association                              */
/* -------------------------------------------------------------------------- */

export function normalizeAssociationPoint(item) {
  if (!item?.id) return null;

  const coordinates = parseGpsString(
    item.gps_asso,
  );

  if (!coordinates) {
    return null;
  }

  const {
    lat,
    lng,
  } = coordinates;

  return {
    /* ---------------------------------------------------------------------- */
    /* Identité                                                               */
    /* ---------------------------------------------------------------------- */

    id: `association-${item.id}`,
    sourceId: item.id,

    type: "association",

    title:
      item.nom_asso ||
      "Association",

    category:
      item.categorie || "",

    /*
     * IMPORTANT :
     * on conserve le HTML.
     */
    description:
      item.description_asso || "",

    /* ---------------------------------------------------------------------- */
    /* Localisation                                                           */
    /* ---------------------------------------------------------------------- */

    lat,
    lng,

    address:
      item.adresse_asso || "",

    mapsHref:
      buildMapsHref(
        lat,
        lng,
      ),

    /* ---------------------------------------------------------------------- */
    /* Contact                                                                */
    /* ---------------------------------------------------------------------- */

    contact:
      item.contact_asso || "",

    contactRole:
      item.fonction_asso || "",

    phone:
      item.tel_asso || "",

    secondaryPhone: "",

    email:
      item.mail_asso || "",

    emails:
      item.mail_asso
        ? [item.mail_asso]
        : [],

    /* ---------------------------------------------------------------------- */
    /* Internet                                                               */
    /* ---------------------------------------------------------------------- */

    website:
      normalizeUrl(
        item.website_asso,
      ),

    secondaryUrl: "",

    facebook:
      normalizeUrl(
        item.facebook_asso,
      ),

    instagram: "",

    /* ---------------------------------------------------------------------- */
    /* Informations complémentaires                                           */
    /* ---------------------------------------------------------------------- */

    tagline: "",

    tags: "",

    services: "",

    accessibility: "",

    paymentMethods: "",

    hours: "",

    additionalInfo: "",

    /* ---------------------------------------------------------------------- */
    /* Métadonnées                                                            */
    /* ---------------------------------------------------------------------- */

    logo:
      item.logo_asso || "",

    isActive:
      item.is_active !== false,

    createdAt:
      item.created_at || null,
  };
}

/* -------------------------------------------------------------------------- */
/*                       Normalisation Commerce                               */
/* -------------------------------------------------------------------------- */

export function normalizeCommercePoint(item) {
  if (!item?.id) return null;

  const coordinates =
    parseSeparateGps(
      item.gps_lat,
      item.gps_lng,
    );

  if (!coordinates) {
    return null;
  }

  const {
    lat,
    lng,
  } = coordinates;

  const address = buildAddress(
    item.adresse,
    [
      item.code_postal,
      item.ville,
    ]
      .filter(Boolean)
      .join(" "),
  );

  return {
    /* ---------------------------------------------------------------------- */
    /* Identité                                                               */
    /* ---------------------------------------------------------------------- */

    id: `commerce-${item.id}`,
    sourceId: item.id,

    type: "commerce",

    title:
      item.nom_commerce ||
      "Commerce",

    category:
      item.categorie || "",

    /*
     * HTML conservé.
     */
    description:
      item.description_commerce || "",

    /* ---------------------------------------------------------------------- */
    /* Localisation                                                           */
    /* ---------------------------------------------------------------------- */

    lat,
    lng,

    address,

    mapsHref:
      buildMapsHref(
        lat,
        lng,
      ),

    /* ---------------------------------------------------------------------- */
    /* Contact                                                                */
    /* ---------------------------------------------------------------------- */

    contact:
      item.contact_nom || "",

    contactRole:
      item.contact_fonction || "",

    phone:
      item.tel || "",

    secondaryPhone: "",

    email:
      item.mail || "",

    emails:
      item.mail
        ? [item.mail]
        : [],

    /* ---------------------------------------------------------------------- */
    /* Internet                                                               */
    /* ---------------------------------------------------------------------- */

    website:
      normalizeUrl(
        item.website,
      ),

    secondaryUrl: "",

    facebook:
      normalizeUrl(
        item.facebook,
      ),

    instagram:
      normalizeUrl(
        item.instagram,
      ),

    /* ---------------------------------------------------------------------- */
    /* Infos métier                                                           */
    /* ---------------------------------------------------------------------- */

    tagline: "",

    tags:
      item.tags || "",

    services:
      item.services || "",

    accessibility:
      item.accessibilite || "",

    paymentMethods:
      item.moyens_paiement || "",

    hours:
      item.horaires_json || null,

    additionalInfo: "",

    siret:
      item.siret || "",

    /* ---------------------------------------------------------------------- */
    /* Métadonnées                                                            */
    /* ---------------------------------------------------------------------- */

    logo:
      item.logo_url || "",

    photo:
      item.photo_url || "",

    isActive:
      item.is_active !== false,

    createdAt:
      item.created_at || null,
  };
}

/* -------------------------------------------------------------------------- */
/*                    Normalisation Service municipal                        */
/* -------------------------------------------------------------------------- */

export function normalizeServicePoint(item) {
  if (!item?.id) return null;

  const coordinates =
    parseGpsString(
      item.gps_service,
    );

  if (!coordinates) {
    return null;
  }

  const {
    lat,
    lng,
  } = coordinates;

  const address = buildAddress(
    item.adresse_service,
    [
      item.cp_service,
      item.ville_service,
    ]
      .filter(Boolean)
      .join(" "),
  );

  const emails =
    normalizeEmails(
      item.mail_service,
    );

  return {
    /* ---------------------------------------------------------------------- */
    /* Identité                                                               */
    /* ---------------------------------------------------------------------- */

    id: `service-${item.id}`,
    sourceId: item.id,

    type: "service",

    title:
      item.nom_service ||
      "Service municipal",

    category:
      item.categorie_service || "",

    serviceType:
      item.type_service || "",

    /*
     * On conserve le HTML complet.
     */
    description:
      item.description_service || "",

    /*
     * L'accroche peut être présentée séparément.
     */
    tagline:
      item.accroche_service || "",

    /* ---------------------------------------------------------------------- */
    /* Localisation                                                           */
    /* ---------------------------------------------------------------------- */

    lat,
    lng,

    address,

    mapsHref:
      buildMapsHref(
        lat,
        lng,
      ),

    /* ---------------------------------------------------------------------- */
    /* Contact                                                                */
    /* ---------------------------------------------------------------------- */

    contact: "",

    contactRole: "",

    phone:
      item.tel_service || "",

    secondaryPhone:
      item.tel_secondaire || "",

    email:
      emails[0] || "",

    emails,

    /* ---------------------------------------------------------------------- */
    /* Internet                                                               */
    /* ---------------------------------------------------------------------- */

    website:
      normalizeUrl(
        item.website_service,
      ),

    secondaryUrl:
      normalizeUrl(
        item.autre_url_service,
      ),

    facebook: "",
    instagram: "",

    /* ---------------------------------------------------------------------- */
    /* Informations pratiques                                                 */
    /* ---------------------------------------------------------------------- */

    tags: "",

    services: "",

    accessibility: "",

    paymentMethods: "",

    hours:
      item.horaires_service || "",

    additionalInfo:
      item.infos_complementaires ||
      "",

    /* ---------------------------------------------------------------------- */
    /* Hiérarchie                                                             */
    /* ---------------------------------------------------------------------- */

    parentId:
      item.parent_id || null,

    order:
      Number(
        item.ordre_affichage || 0,
      ),

    icon:
      item.icone_service || "",

    /* ---------------------------------------------------------------------- */
    /* Métadonnées                                                            */
    /* ---------------------------------------------------------------------- */

    isPublic:
      item.is_public !== false,

    isActive:
      item.is_active !== false,

    createdAt:
      item.created_at || null,
  };
}

/* -------------------------------------------------------------------------- */
/*                                 Tri                                        */
/* -------------------------------------------------------------------------- */

export function sortPoints(points = []) {
  return [...points].sort(
    (a, b) => {
      const titleA =
        String(
          a?.title || "",
        );

      const titleB =
        String(
          b?.title || "",
        );

      return titleA.localeCompare(
        titleB,
        "fr",
        {
          sensitivity: "base",
        },
      );
    },
  );
}